import { observer } from "mobx-react-lite";
import { ReactElement, useState } from "react";
import {
  Box,
  BoxProps,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spacer,
  Switch,
  Tooltip,
} from "@chakra-ui/react";

import {
  APIOption,
  APIType,
  Common,
  ColorType,
  Dictionary,
  isUndefined,
  Lang,
  Logger,
  getRandomHash,
  RGBA,
} from "@noli/core";
import { translate, Words } from "@noli/business";

import ColorPicker from "@/components/ColorPicker";
import Select, { ISelect } from "@/components/Select";
import Property from ".";
import { themeStore } from "@/stores/theme";

export type SchemaValueType = number | string | boolean;
type ISchemaProperty<T extends SchemaValueType> = {
  identifier?: string;
  lang: Lang;
  noTitle?: boolean;
  noSlider?: boolean;
  apiKey: string;
  apiOption: APIOption<T, APIType>;
  value: T | undefined;
  setValue: (opt: { trace: boolean }) => (value: T, apiKey: string) => void;
  dictionary?: Dictionary<Words>;
  selectProps?: Partial<ISelect<string>>;
  captionW?: string;
  inputPadding?: string;
  booleanIcon?: ReactElement;
  booleanCheckedPropsFn?: (checked: boolean) => BoxProps;
  booleanCaption?: string;
  booleanDisabled?: boolean;
  booleanTooltip?: ReactElement;
  colorProps?: Partial<{ opacity: number }>;
} & Common<BoxProps, ISelect<string>>;

function SchemaProperty<T extends SchemaValueType>({
  identifier,
  lang,
  noTitle,
  noSlider,
  apiKey,
  apiOption,
  value,
  setValue,
  dictionary,
  selectProps,
  captionW,
  inputPadding,
  booleanIcon,
  booleanCheckedPropsFn,
  booleanCaption,
  booleanDisabled,
  booleanTooltip,
  colorProps,
  ...others
}: ISchemaProperty<T>) {
  const { borderColor, captionColor } = themeStore.styles;
  const [showTooltip, setShowTooltip] = useState(false);

  function localTranslate(word: string): string {
    return (dictionary ?? {})[word] ?? word;
  }

  const apiWord = localTranslate(apiKey);
  if (isUndefined(value)) {
    value = apiOption.default;
  }
  const commonProps = { w: "85%", h: "32px" };
  const title = noTitle ? null : (
    <Center
      whiteSpace="nowrap"
      w="fit-content"
      minW="35%"
      fontSize="12px"
      color="#939393"
      userSelect="none"
    >
      {translate(apiWord, lang)}
    </Center>
  );
  if (apiOption.type === "int" || apiOption.type === "float" || apiOption.type === "string") {
    const { lower, upper } = apiOption.bounds ?? {};
    if (!isUndefined(lower) && !isUndefined(upper) && !noSlider) {
      const getHandler = (trace: boolean) => (value: number) =>
        setValue({ trace })(value as T, apiKey);
      let step = (upper - lower) * 0.001;
      if (apiOption.type === "int") {
        step = Math.max(1, Math.round(step));
      }
      return (
        <Flex className="c-scheme-property-basic" {...commonProps} {...others}>
          {title}
          <Slider
            h="32px"
            flex={1}
            value={value as number}
            min={lower}
            max={upper}
            color="#333"
            fontSize="12px"
            step={step}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onChange={getHandler(false)}
            onChangeEnd={getHandler(true)}
            focusThumbOnChange={false}
          >
            <SliderTrack>
              <SliderFilledTrack bg="#3fc9a8" />
            </SliderTrack>
            <Tooltip
              hasArrow
              bg="#3fc9a8"
              color="white"
              placement="top"
              fontSize="12px"
              isOpen={showTooltip}
              label={Number((value as number).toFixed(3))}
            >
              <SliderThumb />
            </Tooltip>
          </Slider>
        </Flex>
      );
    }
    return (
      <Property
        identifier={identifier}
        {...commonProps}
        noTitle={noTitle}
        text={translate(apiWord, lang)}
        value={value as string | number}
        setValue={(value) => setValue({ trace: true })(value as T, apiKey)}
        captionW={captionW}
        inputPadding={inputPadding}
        lower={lower}
        upper={upper}
        {...others}
      />
    );
  }
  if (apiOption.type === "color") {
    const getHandler = (trace: boolean): ((color: ColorType) => void) => {
      return (color) => {
        console.log("getHandler color: ", color);
        if (color.type === "SOLID") {
          const rgba = RGBA.from(color.color, parseFloat(color.opacity)).rgbaString;
          setValue({ trace })(rgba as T, apiKey);
        } else {
          Logger.error(
            "internal error occurred, non-SOLID color should not exist in 'SchemaProperty'",
          );
        }
      };
    };

    return (
      <Flex {...commonProps} {...others}>
        {title}
        <Center w="20px" h="32px">
          <ColorPicker
            w="20px"
            h="20px"
            cursor="pointer"
            border="1px"
            borderColor={borderColor}
            originalColor={{
              order: "0",
              type: "SOLID",
              color: value as string,
              opacity: (colorProps?.opacity ?? 1).toString(),
            }}
            colorPickerPopupProps={{
              alphaEditable: false,
              linearEditable: false,
              radialEditable: false,
              popupAlign: { points: ["cr", "cl"], offset: [-128, 45] },
              smartAlign: {
                offset: [-50, 0],
                points: ["br", "bl"],
              },
              safeDistance: 250,
              style: { borderRadius: 0 },
            }}
            colorPreviewProps={{
              style: {
                width: "100%",
                height: "100%",
              },
            }}
            onColorChange={getHandler(false)}
            onColorChangeComplete={getHandler(true)}
          />
        </Center>
      </Flex>
    );
  }
  if (apiOption.type === "choice") {
    return (
      <Select
        {...commonProps}
        value={translate(localTranslate(value as string), lang)}
        options={apiOption.bounds?.choices as string[]}
        optionConverter={(option) => translate(localTranslate(option), lang)}
        onOptionClick={(option) => setValue({ trace: true })(option as T, apiKey)}
        {...others}
        {...(selectProps ?? {})}
      />
    );
  }
  if (apiOption.type === "bool") {
    if (booleanIcon) {
      booleanCheckedPropsFn ??= (checked) => {
        const style = {
          border: "1px",
          borderColor: "#ddd",
        };
        if (checked) {
          return {
            ...style,
            borderColor: "#3fc9a8",
            color: "#3fc9a8",
          };
        }
        return style;
      };
      const checkedProps = booleanCheckedPropsFn(value as boolean);
      return (
        <Box
          cursor="pointer"
          w="32px"
          h="32px"
          color="#333"
          _hover={{
            bgColor: "transparent",
            color: "#3fc9a8",
            borderColor: "#3fc9a8",
          }}
          transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
          onClick={() => setValue({ trace: true })(!value as T, apiKey)}
          {...others}
          {...checkedProps}
        >
          <Center w="100%" h="100%">
            {booleanIcon}
          </Center>
        </Box>
      );
    }

    const hashId = getRandomHash().toString();
    booleanCaption ??= apiKey;
    booleanCaption = translate(booleanCaption, lang);
    return (
      <FormControl display="flex" alignItems="center" {...others}>
        <FormLabel
          mb="0"
          color={captionColor}
          fontSize={others.fontSize ?? "12px"}
          htmlFor={hashId}
          userSelect="none"
        >
          {booleanCaption}
        </FormLabel>
        {booleanTooltip ?? null}
        <Spacer />
        <Switch
          id={hashId}
          sx={{
            "span.chakra-switch__track": { backgroundColor: "#36c19e" },
            "span.chakra-switch__track:not([data-checked])": { backgroundColor: "gray.300" },
          }}
          isChecked={value as boolean}
          disabled={booleanDisabled}
          onChange={(e: { target: { checked: boolean } }) =>
            setValue({ trace: true })(e.target.checked as T, apiKey)
          }
        />
      </FormControl>
    );
  }
  return null;
}

export default observer(SchemaProperty);
